import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { GraduationCap, Users, AlertTriangle, CheckCircle2, BookOpen, ArrowLeft, XCircle, Shield } from 'lucide-react';
import { classroomService, type RosterStudent } from '@/services/classroomService';
import { schoolAdminService, type ClassroomInsight } from '@/services/schoolAdminService';

const RISK_STYLES = {
  high: { bg: 'bg-red-500', light: 'bg-red-100 text-red-700', dark: 'bg-red-900/40 text-red-400', label: 'High Risk' },
  medium: { bg: 'bg-amber-500', light: 'bg-amber-100 text-amber-700', dark: 'bg-amber-900/40 text-amber-400', label: 'Medium Risk' },
  low: { bg: 'bg-emerald-500', light: 'bg-emerald-100 text-emerald-700', dark: 'bg-emerald-900/40 text-emerald-400', label: 'Low Risk' },
};

// ── Roster drill-down ─────────────────────────────────────────────────────────

function RosterPanel({ classroom, dm, onBack }: { classroom: ClassroomInsight; dm: boolean; onBack: () => void }) {
  const [roster, setRoster] = useState<RosterStudent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const cardCls = `border shadow-sm ${dm ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`;
  const titleCls = dm ? 'text-slate-100' : 'text-slate-800';
  const descCls = dm ? 'text-slate-400' : 'text-slate-500';
  // const bodyTextCls = dm ? 'text-slate-300' : 'text-slate-700';

  useEffect(() => {
    classroomService.getClassroomRoster(classroom.id)
      .then(setRoster)
      .catch(() => setError('Could not load roster.'))
      .finally(() => setLoading(false));
  }, [classroom.id]);

  const risk = RISK_STYLES[classroom.riskLevel];

  return (
    <div className="space-y-4">
      {/* Back + header */}
      <div className="flex items-center gap-3">
        <button
          onClick={onBack}
          className={`flex items-center gap-1.5 text-sm transition ${dm ? 'text-slate-400 hover:text-slate-200' : 'text-slate-500 hover:text-blue-600'}`}
          style={{ background: 'none', border: 'none', cursor: 'pointer' }}
        >
          <ArrowLeft className="h-4 w-4" />
          Back to classrooms
        </button>
      </div>

      {/* Classroom header */}
      <div className={`${cardCls} rounded-xl p-5`}>
        <div className="flex items-center gap-4 flex-wrap">
          <div className="h-12 w-12 rounded-xl bg-blue-600 flex items-center justify-center shrink-0">
            <BookOpen className="h-6 w-6 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className={`text-lg font-bold ${titleCls}`}>{classroom.name}</h2>
            <p className={`text-sm ${descCls}`}>
              {[classroom.subject, classroom.gradeLevel ? `Grade ${classroom.gradeLevel}` : null].filter(Boolean).join(' · ')}
            </p>
            {classroom.teachers.length > 0 && (
              <p className={`text-xs mt-0.5 ${descCls}`}>{classroom.teachers.join(', ')}</p>
            )}
          </div>
          <div className="flex gap-3 flex-wrap shrink-0">
            <div className={`text-center px-3 py-2 rounded-lg border ${dm ? 'bg-slate-700 border-slate-600' : 'bg-slate-50 border-slate-100'}`}>
              <p className={`text-lg font-bold ${titleCls}`}>{classroom.studentCount}</p>
              <p className={`text-xs ${descCls}`}>Students</p>
            </div>
            <div className={`text-center px-3 py-2 rounded-lg border ${dm ? 'bg-amber-900/30 border-amber-800' : 'bg-amber-50 border-amber-100'}`}>
              <p className={`text-lg font-bold ${dm ? 'text-amber-400' : 'text-amber-700'}`}>{classroom.openIncidents}</p>
              <p className={`text-xs ${dm ? 'text-amber-500' : 'text-amber-600'}`}>Open</p>
            </div>
            <div className={`text-center px-3 py-2 rounded-lg border ${dm ? 'bg-red-900/30 border-red-800' : 'bg-red-50 border-red-100'}`}>
              <p className={`text-lg font-bold ${dm ? 'text-red-400' : 'text-red-700'}`}>{classroom.criticalIncidents}</p>
              <p className={`text-xs ${dm ? 'text-red-500' : 'text-red-600'}`}>Critical</p>
            </div>
          </div>
          <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${dm ? risk.dark : risk.light}`}>
            {risk.label}
          </span>
        </div>
      </div>

      {/* Roster */}
      <Card className={cardCls}>
        <CardHeader className="pb-2">
          <CardTitle className={`text-base ${titleCls}`}>
            Student Roster ({loading ? '…' : roster.length})
          </CardTitle>
          <CardDescription className={descCls}>All enrolled students in this classroom</CardDescription>
        </CardHeader>
        <CardContent className="pt-0">
          {loading ? (
            <div className="flex items-center justify-center h-40">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500" />
            </div>
          ) : error ? (
            <div className={`flex items-center justify-center h-40 gap-2 text-sm ${dm ? 'text-red-400' : 'text-red-500'}`}>
              <XCircle className="h-4 w-4" /> {error}
            </div>
          ) : roster.length === 0 ? (
            <div className={`flex flex-col items-center justify-center h-40 gap-2 text-sm ${dm ? 'text-slate-500' : 'text-slate-400'}`}>
              <Shield className="h-8 w-8 opacity-40" />
              No students enrolled
            </div>
          ) : (
            <div className="space-y-2">
              {roster.map(r => {
                const name = r.student.user.name || 'Student';
                const initials = name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
                return (
                  <div
                    key={r.student.id}
                    className={`flex items-center gap-3 px-4 py-3 rounded-xl border transition ${dm ? 'bg-slate-800/60 border-slate-700 hover:border-slate-600' : 'bg-white border-slate-100 hover:border-slate-200'}`}
                  >
                    <div className="h-9 w-9 rounded-full bg-blue-600 flex items-center justify-center text-white text-xs font-bold shrink-0">
                      {initials}
                    </div>
                    <div className="min-w-0">
                      <p className={`text-sm font-semibold truncate ${titleCls}`}>{name}</p>
                      <p className={`text-xs truncate ${descCls}`}>{r.student.user.email}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ── Main ClassroomView ────────────────────────────────────────────────────────

export function ClassroomView({ dm }: { dm: boolean }) {
  const [classrooms, setClassrooms] = useState<ClassroomInsight[]>([]);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);
  const [filter, setFilter] = useState<'all' | 'high' | 'medium' | 'low'>('all');
  const [selected, setSelected] = useState<ClassroomInsight | null>(null);

  const cardCls = `border shadow-sm ${dm ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`;
  const titleCls = dm ? 'text-slate-100' : 'text-slate-800';
  const descCls = dm ? 'text-slate-400' : 'text-slate-500';
  const bodyTextCls = dm ? 'text-slate-300' : 'text-slate-700';
  const mutedCls = dm ? 'text-slate-500' : 'text-slate-400';
  const borderCls = dm ? 'border-slate-700' : 'border-slate-200';
  const rowHover = dm ? 'hover:bg-slate-700/40' : 'hover:bg-slate-50';

  const fetch_ = useCallback(async () => {
    setLoading(true);
    try {
      const d = await schoolAdminService.getClassrooms(days);
      setClassrooms(d.classrooms || []);
    } catch {/* */} finally { setLoading(false); }
  }, [days]);

  useEffect(() => { fetch_(); }, [fetch_]);

  if (selected) {
    return <RosterPanel classroom={selected} dm={dm} onBack={() => setSelected(null)} />;
  }

  const filtered = filter === 'all' ? classrooms : classrooms.filter(c => c.riskLevel === filter);

  const summary = {
    total: classrooms.length,
    high: classrooms.filter(c => c.riskLevel === 'high').length,
    medium: classrooms.filter(c => c.riskLevel === 'medium').length,
    low: classrooms.filter(c => c.riskLevel === 'low').length,
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className={`text-lg font-semibold ${titleCls}`}>Classroom Incident View</h2>
        <div className="flex gap-2" data-dm={dm}>
          {[30, 60, 90].map(d => (
            <button key={d} onClick={() => setDays(d)}
              className={`btn-glass btn-glass-pill text-sm ${days === d ? 'btn-glass-blue' : ''}`}>
              {d}d
            </button>
          ))}
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {[
          { label: 'Total Classrooms', value: summary.total, icon: BookOpen, color: 'blue' },
          { label: 'High Risk', value: summary.high, icon: AlertTriangle, color: 'red' },
          { label: 'Medium Risk', value: summary.medium, icon: AlertTriangle, color: 'amber' },
          { label: 'Low Risk', value: summary.low, icon: CheckCircle2, color: 'emerald' },
        ].map((s, i) => (
          <Card key={i} className={cardCls}>
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center gap-2.5">
                <div className={`h-9 w-9 rounded-lg flex items-center justify-center ${dm ? `bg-${s.color}-900/50` : `bg-${s.color}-100`}`}>
                  <s.icon className={`h-4 w-4 text-${s.color}-500`} />
                </div>
                <div>
                  <p className={`text-xs ${descCls}`}>{s.label}</p>
                  <p className={`text-xl font-bold ${titleCls}`}>{s.value}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Filter chips */}
      <div className="flex gap-2 flex-wrap" data-dm={dm}>
        {(['all', 'high', 'medium', 'low'] as const).map(f => (
          <button key={f} onClick={() => setFilter(f)}
            className={`btn-glass btn-glass-pill text-xs capitalize ${filter === f ? 'btn-glass-blue' : ''}`}>
            {f === 'all' ? 'All Classrooms' : `${f} Risk`}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
        </div>
      ) : filtered.length === 0 ? (
        <Card className={cardCls}>
          <CardContent className="flex flex-col items-center justify-center h-48">
            <GraduationCap className={`h-12 w-12 mb-3 ${mutedCls} opacity-40`} />
            <p className={`text-sm ${mutedCls}`}>No classrooms found</p>
          </CardContent>
        </Card>
      ) : (
        <Card className={cardCls}>
          <CardHeader className="pb-2">
            <CardTitle className={`text-base ${titleCls}`}>Classrooms ({filtered.length})</CardTitle>
            <CardDescription className={descCls}>Click a row to view roster · sorted by incident count — last {days} days</CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className={`border-b text-xs font-medium uppercase tracking-wider ${borderCls} ${descCls}`}>
                    <th className="px-4 py-3 text-left">Classroom</th>
                    <th className="px-4 py-3 text-left">Teacher(s)</th>
                    <th className="px-4 py-3 text-center">Students</th>
                    <th className="px-4 py-3 text-center">Incidents</th>
                    <th className="px-4 py-3 text-center">Open</th>
                    <th className="px-4 py-3 text-center">Risk</th>
                  </tr>
                </thead>
                <tbody className={`divide-y ${dm ? 'divide-slate-700' : 'divide-slate-100'}`}>
                  {filtered.map(c => {
                    const risk = RISK_STYLES[c.riskLevel];
                    return (
                      <tr
                        key={c.id}
                        className={`transition-colors cursor-pointer ${rowHover}`}
                        onClick={() => setSelected(c)}
                      >
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div className={`h-8 w-8 rounded-lg flex items-center justify-center shrink-0 ${dm ? 'bg-slate-700' : 'bg-slate-100'}`}>
                              <BookOpen className={`h-4 w-4 ${mutedCls}`} />
                            </div>
                            <div>
                              <p className={`font-medium ${bodyTextCls}`}>{c.name}</p>
                              <p className={`text-xs ${descCls}`}>
                                {[c.subject, c.gradeLevel ? `Grade ${c.gradeLevel}` : null].filter(Boolean).join(' · ')}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <p className={`text-xs ${descCls} truncate max-w-[140px]`}>
                            {c.teachers.length > 0 ? c.teachers.join(', ') : '—'}
                          </p>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <div className="flex items-center justify-center gap-1">
                            <Users className={`h-3.5 w-3.5 ${mutedCls}`} />
                            <span className={bodyTextCls}>{c.studentCount}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className={`font-semibold ${c.totalIncidents > 0 ? 'text-amber-500' : mutedCls}`}>{c.totalIncidents}</span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className={`font-semibold ${c.openIncidents > 0 ? 'text-red-500' : mutedCls}`}>{c.openIncidents}</span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${dm ? risk.dark : risk.light}`}>
                            {risk.label}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
